import $ from "jquery";
import * as preact from "preact";
import * as _ from "underscore";

import * as analytics from "analytics";
import * as epubMetadata from "epubMetadata";
import * as genericModals from "genericModals";
import * as localAPI from "localAPI";
import * as modalController from "modalController";
import * as pendingSubtitles from "pendingSubtitles";
import * as premiumUpsellModal from "premiumUpsellModal";
import readlangClient from "readlangClient";
import * as bookUtils from "shared/bookUtils";
import * as languageMap from "shared/languageMap";
import * as sharedConfig from "shared/sharedConfig";
import * as transcriptUtils from "shared/transcriptUtils";
import { letter as L } from "shared/unicodePatterns";
import * as utils from "shared/utils";
import * as signupModal from "signupModal";
import stores from "stores/stores";
import Dialog from "views/Dialog";
import LanguageSelectView from "views/LanguageSelectView";
import * as walkthrough from "walkthrough";

type Props = {
  appRouter: any;
};

const audioAcceptTypes =
  "audio/mp3,audio/mpeg,audio/mp4,audio/x-m4a,audio/m4a,audio/ogg,audio/wav," +
  "audio/x-wav,audio/flac,audio/aac,audio/webm,.mp3,.m4a,.ogg,.wav,.flac,.aac,.webm,.wma";

const textAcceptTypes =
  "text/plain,text/markdown,application/epub+zip,.srt,.vtt";

/** Shown in place of the upload buttons' labels while a request is in flight. */
const uploadingLabel = "Uploading...";

/** Booleans arrive at the server as the strings "true"/"false". */
const toFormData = (fields: Record<string, any>) => {
  const formData = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    if (value !== undefined) {
      formData.append(key, value);
    }
  }
  return formData;
};

type State = {
  localMode: boolean;
  categories: string[];
  audioUploadedSize?: string;
  audioUploadedPercentage?: number;
  audioUploadedLimit?: string;
  audioUploadedOverLimit?: boolean;
  showAudioPlusUpsell?: boolean;
  plusLimit?: string;
  ready: boolean;
  titleManuallyEdited: boolean;
  title: string;
  author: string;
  category: string;
  publicText: boolean;
  aiGenerated: boolean;
  plainText: string;
  ePub?: File;
  uploading?: boolean;
  uploadingFile?: boolean;
  selectedFileName?: string;
  chosenFileKind?: "text" | "epub" | "audio";
  totalSecondsAudioUploaded?: number;
  audioUploadProgressPercentage?: number;
  srtYouTubeURL: string;
};

class UploadPageView extends preact.Component<Props, State> {
  private fileInputRef = preact.createRef<HTMLInputElement>();
  private srtAudioFileInputRef = preact.createRef<HTMLInputElement>();
  private characterBeforeNewLine = new RegExp(`[${L}].*\\n`, "u");
  private containsCharacter = new RegExp(`[${L}]`, "u");
  private disposeLanguageWatch?: () => void;
  // Mirrors `state.uploading`, but set synchronously: setState only commits on
  // re-render, so a second click arriving before then would still see `false`.
  private uploadInFlight = false;

  constructor(props: Props) {
    super(props);

    this.state = {
      localMode: false,
      categories: bookUtils.categories,
      ready: false,
      titleManuallyEdited: false,
      title: "",
      author: "",
      category: "",
      publicText: false,
      aiGenerated: false,
      plainText: "",
      srtYouTubeURL: "",
    };

    this.disposeLanguageWatch = stores.user.onChange(
      (a) => a.currentLearningLanguage,
      this.onLanguageChange
    );
  }

  async componentDidMount() {
    const localMode = await localAPI.isLocalMode();
    this.setState({ localMode });

    if (stores.user.hasPremiumAccess.value) {
      // Don't let a failed usage lookup break the rest of the upload page —
      // just skip the audio-usage stats
      try {
        const { totalSeconds } = (
          await readlangClient.request("GET", "/api/audioUsage", {})
        ).data;

        const limit = stores.user.hasPremiumPlusAccess.value
          ? sharedConfig.audioUploadLimitPremiumPlus
          : sharedConfig.audioUploadLimitPremium;

        const audioUploadedSize = this.formatAudioSize(totalSeconds);
        const audioUploadedPercentage = Math.round(
          (100 * totalSeconds) / limit
        );
        const audioUploadedLimit = `${Math.floor(limit / 3600)} hours`;
        const audioUploadedOverLimit = totalSeconds > limit;
        const showAudioPlusUpsell = !stores.user.hasPremiumPlusAccess.value;
        const plusLimit = `${sharedConfig.audioUploadLimitPremiumPlus / 3600} hours`;

        this.setState({
          totalSecondsAudioUploaded: totalSeconds,
          audioUploadedSize,
          audioUploadedPercentage,
          audioUploadedLimit,
          audioUploadedOverLimit,
          showAudioPlusUpsell,
          plusLimit,
        });
      } catch (error) {
        console.error("Error fetching audio usage:", error);
      }
    }

    this.initializeLanguageSelector();
  }

  componentWillUnmount() {
    this.disposeLanguageWatch?.();
  }

  formatAudioSize = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    let timeLeft = totalSeconds - hours * 3600;
    const minutes = Math.floor(timeLeft / 60);
    timeLeft = Math.floor(timeLeft - minutes * 60);

    return `${hours ? `${hours} hour${hours === 1 ? "" : "s"} ` : ""}${minutes ? `${minutes} minute${minutes === 1 ? "" : "s"} ` : ""} ${hours === 0 ? `${timeLeft} seconds` : ""}`;
  };

  onLanguageChange = () => {
    this.forceUpdate();
  };

  initializeLanguageSelector = () => {
    const element = document.querySelector(".languageSelector");
    if (element) {
      new LanguageSelectView({
        learningLanguages: true,
        el: $(element),
        field: "currentLearningLanguage",
        hint: "Set the language of this document",
        onChange: () => {},
        english: true,
      });
    }
  };

  onTextInput = (event: Event) => {
    const textInput = (event.target as HTMLTextAreaElement).value;
    this.setState({ plainText: textInput });

    if (
      (!this.state.ready && this.characterBeforeNewLine.test(textInput)) ||
      textInput.length > 3
    ) {
      if (!this.state.ready) {
        this.setState({ ready: true });
      }

      const lines = textInput.split("\n");
      if (!this.state.titleManuallyEdited) {
        _.every(lines, (line) => {
          if (this.containsCharacter.test(line)) {
            this.setState({ title: utils.elide(line, 120) });
            return false;
          }
          return true;
        });
      }
    }
  };

  onTitleEdit = (event: Event) => {
    const title = (event.target as HTMLInputElement).value;
    this.setState({
      title,
      titleManuallyEdited: true,
    });
  };

  onAuthorEdit = (event: Event) => {
    const author = (event.target as HTMLInputElement).value;
    this.setState({ author });
  };

  onCategoryChange = (event: Event) => {
    const category = (event.target as HTMLSelectElement).value;
    this.setState({ category });
  };

  onFileChange = async (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    await this.processFile(file);
  };

  processFile = async (file: File) => {
    if (
      /audio\/(mpeg|mp3|mp4|x-m4a|m4a|ogg|wav|x-wav|flac|aac|webm)/.test(
        file.type
      ) ||
      /\.(mp3|m4a|ogg|wav|flac|aac|webm|wma)$/i.test(file.name)
    ) {
      // Audio uses its own flow and doesn't render the file-chosen confirm UI,
      // so it's safe to set selectedFileName/title up front.
      const fileName = file.name.replace(/\.txt$|\.epub(\.zip)?$/i, "");
      this.setState({
        selectedFileName: file.name,
        title: fileName,
        ready: true,
        chosenFileKind: "audio",
      });
      await this.handleAudioFile(file);
    } else if (
      file.type === "application/epub+zip" ||
      /\.epub$/i.test(file.name) ||
      (file.type === "application/zip" && /\.epub\.zip$/i.test(file.name))
    ) {
      // Defer chosenFileKind/ready/title until the epub is actually loaded —
      // otherwise the confirm UI ("Import book") renders before `ePub` exists
      // and a click would post an empty upload. Rejection paths (oversized,
      // local-mode signup) also avoid setting it so no broken confirm appears.
      await this.handleEpubFile(file);
    } else {
      // Same gating for text: only commit to the confirm UI once the file's
      // contents have been read.
      await this.handleTextFile(file);
    }
  };

  // Reset the form back to the initial "choose a file or paste text" state, so
  // the user can undo a chosen file and pick a different one.
  clearChosenFile = () => {
    this.setState({
      chosenFileKind: undefined,
      selectedFileName: undefined,
      ePub: undefined,
      plainText: "",
      ready: false,
      title: "",
      titleManuallyEdited: false,
      srtYouTubeURL: "",
    });
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = "";
    }
  };

  /**
   * When `subtitles` are provided (SRT-first workflow) they're stashed for the
   * /audioUploaded page, which submits them with the audio file's
   * `POST /api/processAudio` call just as the mp3-first workflow does.
   */
  handleAudioFile = async (file: File, subtitles?: string) => {
    pendingSubtitles.clear();

    if (!stores.user.hasPremiumAccess.value) {
      premiumUpsellModal.show({
        title: "audioUpload",
        heading: "Upgrade To Upload Audio With Auto-Transcription",
        showTitle: false,
      });
      return;
    }

    const perFileLimit = 200;
    if (file.size > perFileLimit * 1024 * 1024) {
      genericModals.alert({
        message: `Sorry, only audio files up to ${perFileLimit}Mb are supported.`,
      });
      return;
    }

    const totalSeconds = this.state.totalSecondsAudioUploaded || 0;
    if (totalSeconds > sharedConfig.audioUploadLimitPremiumPlus) {
      const allowanceHours = Math.floor(
        sharedConfig.audioUploadLimitPremiumPlus / 3600
      );
      genericModals.alert({
        message: (
          <>
            Sorry, you've reached your audio storage allowance of{" "}
            {allowanceHours} hours. If you need more, please get in touch with{" "}
            <a href="mailto:steve@readlang.com">steve@readlang.com</a>.
          </>
        ),
      });
      return;
    }

    if (
      !stores.user.hasPremiumPlusAccess.value &&
      totalSeconds > sharedConfig.audioUploadLimitPremium
    ) {
      const premiumHours = Math.floor(
        sharedConfig.audioUploadLimitPremium / 3600
      );
      const premiumPlusHours = Math.floor(
        sharedConfig.audioUploadLimitPremiumPlus / 3600
      );
      genericModals.alert({
        message: (
          <>
            Sorry, you've reached your audio storage allowance of {premiumHours}{" "}
            hours. <a href="/account">Upgrade to Premium Plus</a> for{" "}
            {premiumPlusHours} hours of storage!
          </>
        ),
      });
      return;
    }

    if (subtitles) {
      pendingSubtitles.stash(subtitles, this.state.selectedFileName);
    }

    this.setState({ uploadingFile: true });
    this.uploadAudio(file);
  };

  handleEpubFile = async (file: File) => {
    // >= to match the server: multer rejects a file once its byte count
    // reaches the limit, so an exactly-at-limit file would 413 there.
    if (file.size >= sharedConfig.epubUploadLimitMB * 1024 * 1024) {
      analytics.trackEventWithProperties("uploadFailed", {
        type: "epub",
        reason: "oversized",
      });
      genericModals.alert({
        message: (
          <>
            Sorry, only epub files under {sharedConfig.epubUploadLimitMB}MB are
            supported. I recommend using{" "}
            <a target="_blank" href="http://calibre-ebook.com/">
              Calibre
            </a>{" "}
            to convert your book to plain text before uploading.
          </>
        ),
      });
      return;
    }

    if (await localAPI.isLocalMode()) {
      signupModal.show({
        heading: "Sign in to import ePub books",
        stores: stores,
      });
      // Local-mode rejection: leave chosenFileKind unset so no confirm UI
      // appears, and the user is prompted to sign up instead.
      return;
    }

    const fileName = file.name.replace(/\.epub(\.zip)?$/i, "");

    // Prefer the epub's own Dublin Core metadata over the (often numeric)
    // filename. Falls back silently on any problem, and never overwrites a
    // value the user already typed.
    const { title: metadataTitle, author: metadataAuthor } =
      await epubMetadata.getEpubMetadata(file);

    // Commit to the confirm UI only now that the epub is accepted — gates the
    // "Import book" button on `ePub` being populated, per the experiment
    // design doc and the code-review feedback.
    const nextState: Partial<State> = {
      selectedFileName: file.name,
      ePub: file,
      chosenFileKind: "epub",
      ready: true,
    };
    if (!this.state.titleManuallyEdited) {
      nextState.title = metadataTitle || fileName;
    }
    if (metadataAuthor && !this.state.author) {
      nextState.author = metadataAuthor;
    }
    this.setState(nextState as State);
  };

  handleTextFile = async (file: File) => {
    const fileName = file.name.replace(/\.(txt|srt|vtt)$/i, "");
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      // Same gating as epub: only show the "Upload text" confirm once we have
      // the text in hand, so a fast click can't post an empty body.
      const nextState: Partial<State> = {
        selectedFileName: file.name,
        plainText: text,
        chosenFileKind: "text",
        ready: true,
      };
      if (!this.state.titleManuallyEdited) {
        nextState.title = fileName;
      }
      this.setState(nextState as State);
    };
    reader.readAsText(file);
  };

  uploadAudio = (file: File) => {
    const formData = new FormData();
    formData.append("audio", file);
    formData.append("language", stores.user.currentLearningLanguage.value);
    formData.append("quality", "medium");

    const xhr = new XMLHttpRequest();
    xhr.open("POST", "/api/audio", true);

    xhr.onload = () => {
      if (xhr.status === 200) {
        this.props.appRouter.navigate("/audioUploaded", { trigger: true });
      } else {
        let errorMessage = undefined;
        if (xhr.status === 413) {
          errorMessage = "Sorry, this file is too large.";
        } else if (xhr.status === 429) {
          errorMessage =
            "Sorry, the audio upload queue is currently full, please try again later.";
        } else {
          errorMessage = "Error uploading audio file: " + xhr.statusText;
        }

        if (errorMessage) {
          genericModals.alert({ message: errorMessage });
          this.setState({ uploadingFile: false });
        }
      }
    };

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const fractionComplete = event.loaded / event.total;
        this.setState({
          audioUploadProgressPercentage: Math.round(fractionComplete * 100),
        });
      }
    };

    xhr.send(formData);
  };

  uploadEPub = async () => {
    if (await localAPI.isLocalMode()) {
      signupModal.show({
        heading: "Sign in to upload books",
        stores: stores,
      });
      return;
    }

    this.showUploadDialog("epub");
    this.upload({
      title: this.state.title,
      author: this.state.author,
      category: this.state.category,
      ePubFile: this.state.ePub,
      language: stores.user.currentLearningLanguage.value,
      useEveryNewLine: true,
      public: this.state.publicText,
      aiGenerated: this.state.aiGenerated,
      source: "uploadEpub",
    });
  };

  uploadPlainText = async () => {
    if (await localAPI.isLocalMode()) {
      signupModal.show({
        heading: "Sign in to upload texts",
        stores: stores,
      });
      return;
    }

    const uploadData = {
      title: this.state.title,
      author: this.state.author,
      category: this.state.category,
      body: this.state.plainText,
      language: stores.user.currentLearningLanguage.value,
      useEveryNewLine: true,
      public: this.state.publicText,
      aiGenerated: this.state.aiGenerated,
      source: "uploadText",
    };

    if (!uploadData.body || uploadData.body.length === 0) {
      alert("Paste or type text to upload first");
      return;
    }

    if (!uploadData.title || uploadData.title.length === 0) {
      alert("Enter a title first");
      return;
    }

    if (this.state.plainText.length > 50000) {
      this.showUploadDialog("txt");
    }
    this.upload(uploadData);
  };

  /**
   * SRT-first workflow, option (a): create a book synced to a YouTube video
   * using the pasted/loaded subtitles as the transcript.
   */
  uploadSRTWithYouTube = async () => {
    if (await localAPI.isLocalMode()) {
      signupModal.show({
        headingHtml: "<h2>Sign in to upload texts</h2>",
        stores: stores,
      });
      return;
    }

    const youTubeID = transcriptUtils.extractYouTubeVideoID(
      this.state.srtYouTubeURL
    );
    if (!youTubeID) {
      alert("YouTube URL or ID not valid. The ID must be 11 characters.");
      return;
    }

    if (!this.state.title || this.state.title.length === 0) {
      alert("Enter a title first");
      return;
    }

    this.upload({
      title: this.state.title,
      author: this.state.author,
      category: this.state.category,
      language: stores.user.currentLearningLanguage.value,
      youTubeID,
      transcript: this.state.plainText,
      public: this.state.publicText,
      aiGenerated: this.state.aiGenerated,
      source: "uploadSRT",
    });
  };

  /**
   * SRT-first workflow, option (b): upload an audio file to go with the
   * subtitles, which are used instead of automatic transcription.
   */
  onSRTAudioFileChange = async (event: Event) => {
    const fileInput = event.target as HTMLInputElement;
    const file = fileInput.files?.[0];
    if (!file) {
      return;
    }
    fileInput.value = "";
    await this.handleAudioFile(file, this.state.plainText);
  };

  /**
   * SRT-first workflow, option (c): strip the cue numbers and timestamps and
   * upload the result as a normal plain text document.
   */
  importSRTAsPlainText = () => {
    this.setState(
      { plainText: transcriptUtils.extractPlainTextSRT(this.state.plainText) },
      this.uploadPlainText
    );
  };

  upload = (data: any) => {
    // Every upload path funnels through here, so this is the one place a
    // double click can be stopped before it creates a duplicate text.
    if (this.uploadInFlight) {
      return;
    }
    this.uploadInFlight = true;
    this.setState({ uploading: true });

    const uploadFinished = () => {
      this.uploadInFlight = false;
      this.setState({ uploading: false });
    };

    const { ePubFile, ...fields } = data;
    // Epubs go as multipart/form-data so the raw bytes go over the wire — a
    // binary string inside JSON would more than double them.
    const body = ePubFile ? toFormData({ ...fields, ePub: ePubFile }) : fields;

    readlangClient.request(
      "POST",
      "/api/book",
      body,
      (responseData) => {
        uploadFinished();
        if (responseData.success) {
          modalController.cancel();
          this.props.appRouter.navigate("/myTexts/queue", { trigger: true });
          walkthrough.setAchievement("uploadText");
        } else {
          if (responseData.error) {
            alert(responseData.error);
          } else {
            alert("error: " + JSON.stringify(responseData));
          }
        }
      },
      (error) => {
        uploadFinished();
        modalController.cancel();
        // readlangClient's error callback receives { status, data,
        // alreadyNotified }, where `data` is the parsed server response body
        // and `alreadyNotified` means readlangClient already showed a global
        // notification (network loss after retries, 5xx).
        const serverError = error?.data?.error;
        let reason = "server";
        if (error.alreadyNotified) {
          // Nothing more to show — just unlock the form so the user can retry.
          reason = error.status === 0 ? "network" : "server";
        } else if (error.status === 413) {
          reason = "oversized";
          alert(
            ePubFile
              ? `Sorry, only epub files under ${sharedConfig.epubUploadLimitMB}MB are supported.`
              : "Sorry, this text is too large to upload."
          );
        } else if (error?.data?.epubConvertError) {
          reason = "drm";
          genericModals.alert({
            message: (
              <>
                We couldn't read this ePub — it's most likely{" "}
                <strong>copy-protected (DRM)</strong> (e.g. a book from Kindle,
                Google Play, or Kobo).{" "}
                <a
                  target="_blank"
                  href="https://forum.readlang.com/t/upload-novels-to-read-including-ones-with-drm/39"
                >
                  Here's how to remove the DRM
                </a>
                , or use{" "}
                <a target="_blank" href="http://calibre-ebook.com/">
                  Calibre
                </a>{" "}
                to export a plain-text version and upload that instead.
              </>
            ),
          });
        } else if (serverError) {
          // Show the server's human-readable message rather than raw JSON.
          genericModals.alert({ message: serverError });
        } else {
          alert(
            error.status + " error uploading file: " + JSON.stringify(error)
          );
        }
        analytics.trackEventWithProperties("uploadFailed", {
          type: ePubFile ? "epub" : "text",
          reason,
          source: data.source,
        });
      }
    );
  };

  showUploadDialog = (type: string) => {
    if (type === "epub") {
      modalController.show(
        <Dialog heading="Uploading and converting ePub">
          <p>Please wait, this may take a minute...</p>
          <p style={{ textAlign: "center" }}>
            <img src="/content/bigLoadingThrobber.gif" />
          </p>
          <p>
            <small>
              (If this doesn't work for you, please get in touch with{" "}
              <a href="mailto:steve@readlang.com">steve@readlang.com</a>. Or{" "}
              <a href="http://calibre-ebook.com/">Use Calibre</a> to convert it
              to plain text first.)
            </small>
          </p>
        </Dialog>,
        {
          clickToCancel: false,
          closeButton: false,
        }
      );
      return;
    }

    modalController.show(
      <Dialog heading="Uploading plain text">
        <p>Please wait, this may take a minute...</p>
        <p style={{ textAlign: "center" }}>
          <img src="/content/throbber.gif" />
        </p>
      </Dialog>,
      {
        clickToCancel: false,
        closeButton: false,
      }
    );
  };

  render() {
    if (this.state.uploadingFile) {
      return (
        <div>
          <p>
            Uploading audio file...
            {this.state.audioUploadProgressPercentage
              ? ` ${this.state.audioUploadProgressPercentage}%`
              : ""}
          </p>
        </div>
      );
    }

    const currentLang = stores.user.currentLearningLanguage.value;
    const supportsAudio = languageMap.map[currentLang]?.whisper === true;
    const languageName = languageMap.map[currentLang]?.english || currentLang;
    const srtDetected =
      !this.state.ePub && transcriptUtils.isSRTTranscript(this.state.plainText);

    // Once a text/epub file is chosen we collapse the "paste text / choose
    // file" chooser into a focused confirm step, so it's clear what stage the
    // user is at and what to do next. (Audio uploads run their own flow.)
    // Subtitle files are the exception: they get the SRT options panel below
    // the text area instead of a plain "Upload text" confirm.
    const fileChosen =
      !srtDetected &&
      (this.state.chosenFileKind === "epub" ||
        this.state.chosenFileKind === "text");

    return (
      <div class="uploadPage">
        <h2 class="spacing">
          <i class="icon-circle-arrow-up"></i> Upload{" "}
          <span class="languageSelector"></span> Text
          {supportsAudio ? " or Audio" : ""}
        </h2>

        {!this.state.ready && (
          <small>
            <i>
              Upload texts to read privately, or to share with the community.
            </i>
          </small>
        )}

        <div class="addDocumentForm">
          {this.state.ready && (
            <table class="table">
              <tr>
                <td style={{ width: "70px" }}>Title:</td>
                <td>
                  <input
                    type="text"
                    name="title"
                    data-testid="upload-title-input"
                    value={this.state.title}
                    onInput={this.onTitleEdit}
                  />
                </td>
              </tr>
              <tr>
                <td style={{ width: "70px" }}>Author:</td>
                <td>
                  <input
                    type="text"
                    name="author"
                    maxLength={bookUtils.maxAuthorLength}
                    value={this.state.author}
                    onInput={this.onAuthorEdit}
                  />
                </td>
              </tr>
              <tr>
                <td style={{ width: "70px" }}>Category:</td>
                <td>
                  <select
                    class="categorySelect"
                    onChange={this.onCategoryChange}
                  >
                    <option class="dummyOption">Choose category...</option>
                    {this.state.categories.map((category) => (
                      <option key={category} value={category}>
                        {bookUtils.categoryLabel(category)}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
              {!this.state.localMode && (
                <>
                  <tr>
                    <td style={{ width: "70px" }}>Public:</td>
                    <td>
                      <label>
                        <input
                          type="checkbox"
                          name="public"
                          checked={this.state.publicText}
                          onChange={(e) =>
                            this.setState({
                              publicText: (e.target as HTMLInputElement)
                                .checked,
                            })
                          }
                        />
                        Please select to share with the Readlang community,{" "}
                        <strong>
                          but only if the text is public domain or if sharing is
                          permitted by the copyright holder!
                        </strong>
                      </label>
                    </td>
                  </tr>
                  <tr>
                    <td style={{ width: "70px" }}>AI:</td>
                    <td>
                      <label>
                        <input
                          type="checkbox"
                          name="aiGenerated"
                          checked={this.state.aiGenerated}
                          onChange={(e) =>
                            this.setState({
                              aiGenerated: (e.target as HTMLInputElement)
                                .checked,
                            })
                          }
                        />
                        Select if the text was generated by AI
                      </label>
                    </td>
                  </tr>
                </>
              )}
            </table>
          )}

          {fileChosen && (
            <div class="fileChosen">
              <p class="ePubInfo">
                <small>Ready to upload:</small>
                <br />
                <span class="filename">{this.state.selectedFileName}</span>
              </p>
              <div class="buttonRow">
                <button
                  class="prominent payButton"
                  data-testid="upload-confirm-button"
                  disabled={this.state.uploading}
                  onClick={
                    this.state.chosenFileKind === "epub"
                      ? this.uploadEPub
                      : this.uploadPlainText
                  }
                >
                  {this.state.uploading
                    ? uploadingLabel
                    : this.state.chosenFileKind === "epub"
                      ? "Import book"
                      : "Upload text"}
                </button>
                <button
                  type="button"
                  data-testid="upload-cancel-button"
                  onClick={this.clearChosenFile}
                >
                  Cancel
                </button>
              </div>
              {this.state.chosenFileKind === "epub" &&
                !this.state.localMode && (
                  <p class="suggestion">
                    <small>
                      Trouble uploading?{" "}
                      <a href="http://calibre-ebook.com/">Use Calibre</a> (free)
                      to convert almost any book to plain text first. Books with{" "}
                      <a
                        target="_blank"
                        href="https://forum.readlang.com/t/upload-novels-to-read-including-ones-with-drm/39"
                      >
                        DRM
                      </a>{" "}
                      must be converted before uploading.
                    </small>
                  </p>
                )}
            </div>
          )}

          <div
            class={utils.cn({
              left: true,
              disabled: !!this.state.ePub,
              chooserColumnHidden: fileChosen,
            })}
          >
            <h3 class="spacing">
              <small>either</small> Paste Text
            </h3>
            <textarea
              rows={12}
              class="plainTextInput"
              data-testid="upload-plain-text-input"
              value={this.state.plainText}
              onInput={this.onTextInput}
              onPaste={() =>
                setTimeout(
                  () =>
                    this.onTextInput({
                      target: { value: this.state.plainText },
                    } as any),
                  0
                )
              }
              disabled={!!this.state.ePub}
            />
            {this.state.ready && !this.state.ePub && !srtDetected && (
              <p>
                <button
                  class="prominent payButton uploadPlainText"
                  data-testid="upload-plain-text-button"
                  disabled={this.state.uploading}
                  onClick={this.uploadPlainText}
                >
                  {this.state.uploading ? uploadingLabel : "Upload Plain Text"}
                </button>
              </p>
            )}
            {srtDetected && (
              <div class="srtOptionsPanel" data-testid="srt-options-panel">
                <p>
                  <strong>This looks like a subtitle file (SRT/WebVTT).</strong>{" "}
                  How would you like to import it?
                </p>
                <p>
                  <input
                    type="text"
                    placeholder="Paste YouTube video URL"
                    data-testid="srt-youtube-url-input"
                    value={this.state.srtYouTubeURL}
                    onInput={(event) =>
                      this.setState({
                        srtYouTubeURL: (event.target as HTMLInputElement).value,
                      })
                    }
                  />
                  {this.state.srtYouTubeURL.trim().length > 0 && (
                    <button
                      class="srtYouTubeButton"
                      data-testid="srt-youtube-button"
                      disabled={this.state.uploading}
                      onClick={this.uploadSRTWithYouTube}
                    >
                      {this.state.uploading ? (
                        uploadingLabel
                      ) : (
                        <>
                          <i class="icon-plus"></i> Sync With YouTube Video
                        </>
                      )}
                    </button>
                  )}
                </p>
                <p>
                  <input
                    type="file"
                    class="srtAudioFileInput"
                    accept={audioAcceptTypes}
                    ref={this.srtAudioFileInputRef}
                    onChange={this.onSRTAudioFileChange}
                  />
                  <small>or</small>{" "}
                  <button
                    data-testid="srt-audio-button"
                    onClick={() => this.srtAudioFileInputRef.current?.click()}
                  >
                    <i class="icon-upload"></i> Attach An Audio File
                  </button>{" "}
                  <small>
                    (.mp3, .m4a, etc.) — your subtitles will be used instead of
                    automatic transcription
                  </small>
                </p>
                <p>
                  <small>or</small>{" "}
                  <button
                    data-testid="srt-plain-text-button"
                    disabled={this.state.uploading}
                    onClick={this.importSRTAsPlainText}
                  >
                    <i class="icon-file-text"></i> Import As Plain Text
                  </button>{" "}
                  <small>(removes the timestamps)</small>
                </p>
              </div>
            )}
          </div>

          <div
            class={utils.cn({
              right: true,
              disabled: this.state.plainText.length > 0 && !this.state.ePub,
              chooserColumnHidden: fileChosen,
            })}
          >
            <h3 class="spacing">
              <small>or</small> Choose File
            </h3>
            <div
              class="fileUpload prominent"
              title="Click to choose file, or drag and drop a .txt or .epub onto this button"
            >
              <span>{this.state.selectedFileName || "Choose File"}</span>
              <input
                type="file"
                class="upload"
                accept={
                  supportsAudio
                    ? `${textAcceptTypes},${audioAcceptTypes}`
                    : textAcceptTypes
                }
                ref={this.fileInputRef}
                onChange={this.onFileChange}
                disabled={this.state.uploadingFile}
              />
            </div>
            <p>
              <strong>.txt</strong>, <strong>.epub</strong> (DRM-free),{" "}
              <strong>.srt</strong>
              {supportsAudio && (
                <>
                  {" "}
                  and audio files (<strong>.mp3</strong>, <strong>.m4a</strong>,
                  etc.)
                </>
              )}{" "}
              supported
              <br />
            </p>

            {!this.state.localMode && (
              <>
                <p class="suggestion">
                  <small>
                    Trouble uploading?{" "}
                    <a href="http://calibre-ebook.com/">Use Calibre</a> (free)
                    to convert almost any book to plain text or epub first.
                  </small>
                </p>
                <p class="suggestion">
                  <small>
                    Want to upload Kindle or other purchased books?{" "}
                    <a
                      target="_blank"
                      href="https://forum.readlang.com/t/upload-novels-to-read-including-ones-with-drm/39"
                    >
                      Upload books with DRM
                    </a>
                    .
                  </small>
                </p>
              </>
            )}

            {this.state.localMode && (
              <div class="callout">
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    signupModal.show({
                      heading: "Sign in to import ePub books",
                      stores: stores,
                    });
                  }}
                >
                  Sign Up For Free
                </a>{" "}
                to upload DRM-free ePub books.
              </div>
            )}

            {supportsAudio ? (
              this.state.audioUploadedSize && (
                <p>
                  <small>
                    Audio files can be up to a maximum of 200MB and 2 hours
                    each. You've used
                    <strong> {this.state.audioUploadedSize}</strong> out of your
                    <strong> {this.state.audioUploadedLimit}</strong> audio
                    storage allowance.
                    {this.state.audioUploadedOverLimit && (
                      <>
                        {" "}
                        <strong>Over limit!</strong> Delete books with attached
                        mp3 files to free space to upload more.
                      </>
                    )}
                    {this.state.showAudioPlusUpsell && (
                      <>
                        {" "}
                        <a href="/account">Upgrade to Readlang Plus</a> for{" "}
                        {this.state.plusLimit} of audio storage.
                      </>
                    )}
                  </small>
                </p>
              )
            ) : (
              <p>
                <small>Audio upload not supported for {languageName}</small>
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default UploadPageView;
